import { PersonalTimesheetLegacyRedirect } from './components/personal-timesheets/PersonalTimesheetLegacyRedirect';
import React, { lazy, Suspense, useCallback, useLayoutEffect } from 'react';
import { clearThumbnailCache } from './utils/thumbnailCache';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useAuthContext } from './components/AuthProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import { Layout } from './components/Layout';
import { DashboardEntryRoute } from './components/DashboardEntryRoute';
import { SplashScreen } from './components/SplashScreen';
import { useMaintenanceStatus } from './hooks/useMaintenanceStatus';
import { PageTitleProvider } from './context/PageTitleContext';
import { PageLayoutProvider } from './context/PageLayoutContext';
import { ProjectWorkspaceProvider } from './context/ProjectWorkspaceContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { LoginPage } from './pages/LoginPage';
import { AppErrorPage } from './pages/AppErrorPage';
import { AdminModuleKey } from './types';
import { canAccessDuringMaintenance, getDefaultModulePath, hasModuleAccess } from './utils/modulePermissions';
import { isPermissionPreview } from './services/permissionPreviewSession';
import { PermissionPreviewBanner } from './components/PermissionPreviewBanner';

const MyHoursPage = lazy(() => import('./pages/MyHoursPage').then(m => ({ default: m.MyHoursPage })));
const BidsPage = lazy(() => import('./pages/BidsPage'));
const Dashboard            = lazy(() => import('./components/dashboard/PersonalHome').then(m => ({ default: m.PersonalHome })));
const TimesheetPage        = lazy(() => import('./pages/TimesheetPage').then(m => ({ default: m.TimesheetPage })));
const ProjectsPage         = lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const SchedulePage         = lazy(() => import('./pages/SchedulePage').then(m => ({ default: m.SchedulePage })));
const PeoplePage           = lazy(() => import('./pages/PeoplePage').then(m => ({ default: m.PeoplePage })));
const ReportsPage          = lazy(() => import('./pages/ReportsHomePage').then(m => ({ default: m.ReportsHomePage })));
const ExportPage           = lazy(() => import('./pages/ExportPage').then(m => ({ default: m.ExportPage })));
const SettingsPage         = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AccountSettingsPage  = lazy(() => import('./pages/AccountSettingsPage').then(m => ({ default: m.AccountSettingsPage })));
const TasksPage            = lazy(() => import('./components/dashboard/PersonalHome').then(m => ({ default: m.PersonalHome })));
const MyPeoplePage         = lazy(() => import('./pages/MyPeoplePage').then(m => ({ default: m.MyPeoplePage })));
const ProjectWorkspacePage = lazy(() => import('./pages/ProjectWorkspacePage').then(m => ({ default: m.ProjectWorkspacePage })));
const SecurityCenterPage   = lazy(() => import('./pages/SecurityCenterPage').then(m => ({ default: m.SecurityCenterPage })));
const EmployeeHoursPage    = lazy(() => import('./pages/EmployeeHoursPage').then(m => ({ default: m.EmployeeHoursPage })));
const InProgressPage       = lazy(() => import('./pages/InProgressPage').then(m => ({ default: m.InProgressPage })));
const ForecastPrototypePage = lazy(() => import('./pages/ForecastPrototypePage').then(m => ({ default: m.ForecastPrototypePage })));
const WarehousePage        = lazy(() => import('./pages/WarehousePage').then(m => ({ default: m.WarehousePage })));
const WarehouseRequestsPage = lazy(() => import('./pages/WarehouseRequestsPage').then(m => ({ default: m.WarehouseRequestsPage })));
const ScanUnitPage         = lazy(() => import('./pages/ScanUnitPage').then(m => ({ default: m.ScanUnitPage })));
const ActivityLogPage      = lazy(() => import('./pages/ActivityLogPage').then(m => ({ default: m.ActivityLogPage })));
const TicketSignaturePage = lazy(() => import('./pages/TicketSignaturePage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthContext();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  return <>{children}</>;
}

function ModuleRoute({
  children,
  moduleKey,
  ownerOnly = false,
  adminOnly = false,
  developerOnly = false,
}: {
  children: React.ReactNode;
  moduleKey: AdminModuleKey | AdminModuleKey[];
  ownerOnly?: boolean;
  adminOnly?: boolean;
  developerOnly?: boolean;
}) {
  const { user, loading } = useAuthContext();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  const isDeveloper = Number(user.is_developer || 0) === 1;
  const isOperator = user.role === 'admin' && Number(user.is_operator || user.is_owner_admin || 0) === 1;
  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/error/forbidden" replace />;
  }
  if (developerOnly && !isDeveloper) {
    return <Navigate to="/error/forbidden" replace />;
  }
  if (ownerOnly && !isDeveloper && !isOperator) {
    return <Navigate to="/error/forbidden" replace />;
  }
  const moduleKeys = Array.isArray(moduleKey) ? moduleKey : [moduleKey];
  if (moduleKeys.includes('security') && (isDeveloper || isOperator)) {
    return <>{children}</>;
  }
  if (!moduleKeys.some((key) => hasModuleAccess(user, key, 'view'))) {
    return <Navigate to="/error/forbidden" replace />;
  }
  return <>{children}</>;
}

function useMaintenanceGuard(isDeveloper: boolean, canBypass: boolean, offlineMode: boolean) {
  // Shares the single maintenance poller with MaintenanceBanner (60s, paused
  // while the tab is hidden). Bypassing users don't subscribe at all.
  const status = useMaintenanceStatus(!(isDeveloper || canBypass || offlineMode));
  return status?.status === 'active' ? status.window : null;
}

function App() {
  const { user, loading, logout, offlineMode, error } = useAuthContext();
  const preview = isPermissionPreview();
  const thumbnailScope = JSON.stringify([user?.id, user?.role, user?.access_scope, user?.module_permissions, user?.sub_module_permissions, user?.permission_preview, preview]);
  // Clear before new account/permission-scoped image refs attach.
  useLayoutEffect(() => () => clearThumbnailCache(), [thumbnailScope]);
  const isDeveloper = Number(user?.is_developer || 0) === 1;
  const canBypassMaintenance = canAccessDuringMaintenance(user) || (preview && Boolean(user?.permission_preview));
  const maintenanceWindow = useMaintenanceGuard(isDeveloper, canBypassMaintenance, offlineMode);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  // Auto-logout after 30 minutes of inactivity. Warn the user 60s before.
  // Developers stay signed in (long debugging sessions) — toggle below if you
  // prefer to enforce it for everyone.
  const handleInactivityWarn = useCallback((msRemaining: number) => {
    toast(
      `You'll be signed out in ${Math.round(msRemaining / 1000)}s due to inactivity.`,
      { id: 'inactivity-warn', icon: '⏱', duration: 8_000 },
    );
  }, []);
  const handleInactivityTimeout = useCallback(async () => {
    toast('Signed out due to inactivity.', { id: 'inactivity-logout', icon: '🔒' });
    // Clear the stale timestamp so the next login doesn't immediately
    // re-trigger the inactivity guard.
    try { localStorage.removeItem('forxa-last-activity'); } catch { /* ignore */ }
    await logout();
  }, [logout]);
  useInactivityLogout({
    enabled: !!user && !isDeveloper && !preview,
    timeoutMs: 30 * 60 * 1000,
    warnBeforeMs: 60_000,
    onWarn: handleInactivityWarn,
    onTimeout: handleInactivityTimeout,
  });

  if (loading) return <SplashScreen />;
  if (preview && !user) return <><PermissionPreviewBanner user={null} /><p role="alert" className="p-6">{error}</p></>;

  if (maintenanceWindow && currentPath !== '/login') {
    return (
      <LanguageProvider preferredLanguage={user?.preferred_language}>
        <ThemeProvider preference={user?.theme_preference}>
          <Router>
            <AppErrorPage forceMaintenance={maintenanceWindow} />
          </Router>
        </ThemeProvider>
      </LanguageProvider>
    );
  }

  return (
    <ErrorBoundary>
      {preview && <PermissionPreviewBanner user={user} />}
      <LanguageProvider preferredLanguage={user?.preferred_language}>
      <ThemeProvider preference={user?.theme_preference}>
      <PageLayoutProvider>
      <PageTitleProvider>
      <ProjectWorkspaceProvider>
        <Toaster position="top-right" toastOptions={{ duration: 3500 }} containerStyle={{ zIndex: 99999 }} />
        <Router>
          <Suspense fallback={<SplashScreen />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sign-ticket" element={<TicketSignaturePage />} />
            <Route path="/error/:kind" element={<AppErrorPage />} />
            {/* Deliberately outside ProtectedRoute/Layout — a QR scan should land
                on a fast, full-screen mobile page, not the full app shell. It
                still requires login (handles its own redirect + returnTo). */}
            <Route path="/scan" element={<ScanUnitPage />} />
            <Route path="/scan/t/:token" element={<ScanUnitPage />} />
            <Route path="/scan/:unitId" element={<ScanUnitPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout user={user} onLogout={logout}>
                    <Routes>
                      <Route path="/dashboard"    element={<DashboardEntryRoute user={user}><ModuleRoute moduleKey="dashboard"><Dashboard user={user} /></ModuleRoute></DashboardEntryRoute>} />
                      <Route path="/bids/:id?" element={<ModuleRoute moduleKey="estimates"><BidsPage user={user} /></ModuleRoute>} />
                      <Route path="/estimator" element={<ModuleRoute moduleKey="estimates"><BidsPage user={user} workspace /></ModuleRoute>} />
                      <Route path="/my-hours" element={<MyHoursPage user={user} />} />
                      <Route path="/timesheets"   element={<PersonalTimesheetLegacyRedirect><ModuleRoute moduleKey="timesheets"><TimesheetPage user={user} /></ModuleRoute></PersonalTimesheetLegacyRedirect>} />
                      <Route path="/projects"     element={<ModuleRoute moduleKey={['projects', 'documents', 'drawings', 'rfis', 'free_reports']}><ProjectsPage user={user} offlineMode={offlineMode} /></ModuleRoute>} />
                      <Route path="/schedule"     element={<ModuleRoute moduleKey="schedule"><SchedulePage user={user} /></ModuleRoute>} />
                      {/* Field Logs and Control now live inside /reports */}
                      <Route path="/field-logs" element={<Navigate to="/reports?topview=field_reports" replace />} />
                      <Route path="/control"    element={<Navigate to="/reports?topview=control" replace />} />
                      <Route
                        path="/projects/:id/*"
                        element={
                          <ModuleRoute moduleKey={['projects', 'timesheets', 'reports', 'schedule', 'field_logs', 'control', 'warehouse', 'documents', 'drawings', 'rfis', 'free_reports']}>
                            <ProjectWorkspacePage user={user} offlineMode={offlineMode} />
                          </ModuleRoute>
                        }
                      />
                      <Route path="/people"       element={<ModuleRoute moduleKey="people"><PeoplePage user={user} /></ModuleRoute>} />
                      <Route path="/reports"         element={<ModuleRoute moduleKey={['reports', 'field_logs', 'free_reports', 'control']}><ReportsPage user={user} /></ModuleRoute>} />
                      <Route path="/employee-hours" element={<ModuleRoute moduleKey="employee_hours"><EmployeeHoursPage user={user} /></ModuleRoute>} />
                      <Route path="/export"       element={<ModuleRoute moduleKey="export"><ExportPage /></ModuleRoute>} />
                      <Route path="/security-center" element={<ModuleRoute moduleKey="security"><SecurityCenterPage user={user} /></ModuleRoute>} />
                      <Route path="/activity-log" element={<ModuleRoute moduleKey="security" ownerOnly><ActivityLogPage /></ModuleRoute>} />
                      <Route path="/settings"     element={<ModuleRoute moduleKey="settings"><SettingsPage user={user} /></ModuleRoute>} />
                      <Route path="/warehouse"    element={<ModuleRoute moduleKey="warehouse">{user && <WarehousePage user={user} />}</ModuleRoute>} />
                      <Route path="/requests/:id?" element={<ModuleRoute moduleKey="requests">{user && <WarehouseRequestsPage user={user} />}</ModuleRoute>} />
                      <Route path="/warehouse/requests/:id?" element={<ModuleRoute moduleKey="requests">{user && <WarehouseRequestsPage user={user} />}</ModuleRoute>} />
                      <Route path="/in-progress"  element={<ModuleRoute moduleKey="in_progress"><InProgressPage user={user} /></ModuleRoute>} />
                      <Route path="/in-progress/forecast" element={<ModuleRoute moduleKey="in_progress"><ForecastPrototypePage user={user} /></ModuleRoute>} />
                      <Route path="/in-progress/warehouse" element={<Navigate to="/warehouse" replace />} />
                      <Route path="/account"      element={<AccountSettingsPage user={user} />} />
                      <Route path="/inicio"       element={<TasksPage user={user} />} />
                      <Route path="/home" element={user && hasModuleAccess(user, 'estimates') && !hasModuleAccess(user, 'dashboard') && !hasModuleAccess(user, 'projects') ? <BidsPage user={user} workspace /> : <TasksPage user={user} />} />
                      <Route path="/equipo"       element={<MyPeoplePage />} />
                      {/* Legacy redirects */}
                      <Route path="/"               element={<Navigate to={getDefaultModulePath(user)} />} />
                      <Route path="*" element={<AppErrorPage />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          </Suspense>
        </Router>
      </ProjectWorkspaceProvider>
      </PageTitleProvider>
      </PageLayoutProvider>
      </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
